/**
 * Générateur — Définir l'opposé et la valeur absolue d'un nombre.
 * Attendu ID : 911 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 911.json) : « Définir l'opposé et la valeur
 * absolue d'un nombre. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « trou » — De l'addition à trou à l'opposé (niveaux 1 et 2)
 *     texte_trou_drag : quatre égalités a + {{opposé}} = 0, valeurs absolues
 *     toutes distinctes (les étiquettes pièges survivent au filtre) ;
 *     texte (saisie) : l'opposé d'un nombre, zéro tiré volontairement.
 *
 *   Carte « absolue » — La valeur absolue d'un nombre (niveaux 1 et 2)
 *     texte (saisie) : la valeur absolue d'un entier ou d'un décimal ;
 *     qcm_unique : la question alterne entre l'opposé et la valeur absolue,
 *     distracteurs = le nombre recopié et les deux décimales échangées ;
 *     tableau_vf : quatre lignes tirées parmi six gabarits à prédicat variable.
 *
 *   Carte « double » — L'opposé de l'opposé (niveaux 1 et 2)
 *     texte_trou_saisie (chaînes nombre → opposé → opposé de l'opposé, le cas 0
 *     en rotation), texte_trou_drag (la règle adossée à un exemple tiré),
 *     qcm_unique (opposé de l'opposé / opposé de l'opposé de l'opposé).
 *
 *   Carte « droite » — Distance à zéro sur la droite graduée (niveaux 1 et 3)
 *     Élément numberline natif, plage -10..10, six points lettrés dont deux
 *     paires d'abscisses opposées par construction. texte_trou_drag,
 *     tableau_vf (cinq gabarits à prédicat variable), qcm_multiple (les deux
 *     paires opposées, compte annoncé), texte (saisie de la distance à zéro).
 *
 *   Carte « tableauva » — Nombre, valeur absolue et opposé (niveaux 3 et 4)
 *     texte_trou_saisie, une colonne par ligne de phrase : la ligne masquée du
 *     tableau (le nombre, sa valeur absolue ou son opposé) tourne à chaque
 *     tirage ; second format « retrouver » (niveau 4) où la valeur absolue est
 *     la seule donnée, la consigne annonçant que les nombres sont négatifs.
 *
 *   Carte « enigme » — L'énigme du nombre caché (niveau 5)
 *     texte (saisie) : décimal à deux décimales, contraintes croisées sur le
 *     signe de l'opposé, l'encadrement de la valeur absolue et les deux
 *     chiffres décimaux. Solution unique vérifiée par énumération à la
 *     génération.
 *
 * Spécification (911.json) :
 *   Bornes appliquées : nombre_traite -10..10 (valeurs manipulées en centièmes
 *     entiers, |v| ≤ 1000) ; nombre_de_decimales 0..2 ; dénomination de la
 *     valeur absolue tirée par SÉRIE (« valeur absolue » ou « distance à
 *     zéro »), jamais les deux dans la même série ; notation à barres
 *     verticales jamais employée ni exigée ; zéro tiré volontairement (une
 *     fois sur huit en saisie, une série d'égalités sur trois, une colonne de
 *     tableau sur trois) ; nombre_de_colonnes_du_tableau 4..8 (4..5 dans la
 *     configuration « retrouver », qui masque deux lignes) ;
 *     ligne_masquee_du_tableau 0..2, les trois configurations tirées ;
 *     affichage_du_signe_des_positifs : le + devant un positif est tiré par
 *     cellule affichée (jamais dans une valeur attendue, la tolérance
 *     numérique de la saisie acceptant les deux écritures).
 *   Exclusions : aucune fraction ; aucun nombre au-delà de 10 en valeur
 *     absolue ; aucun nombre à plus de deux décimales ; aucune barre verticale ;
 *     aucune énigme à deux solutions (énumération exhaustive à la génération) ;
 *     aucune valeur attendue sous la forme d'un zéro signé (cent(0) = « 0 »).
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code ou _meta.params.erreurVisee) :
 *     « Répondre -3,4 à la valeur absolue de -3,4 : recopier le nombre »
 *         → valeur_absolue_recopie (absolue_qcm_unique, absolue_vf, absolue_texte)
 *     « Croire qu'un nombre et son opposé ont des valeurs absolues différentes »
 *         → valeurs_absolues_differentes (absolue_vf, droite_vf)
 *     « Écrire l'opposé de 0 avec un signe »
 *         → oppose_de_zero_signe (absolue_vf, trou_oppose_texte)
 *     « Prendre l'opposé d'un nombre négatif pour ce nombre lui-même »
 *         → oppose_negatif_inchange (trou_egalites, trou_oppose_texte,
 *           absolue_qcm_unique, absolue_vf, droite_abscisse)
 *     « Garder l'ordre des bornes en passant à l'opposé »
 *         → ordre_conserve_par_oppose (absolue_vf)
 *     « Confondre le chiffre des dixièmes et celui des centièmes »
 *         → dixiemes_centiemes (absolue_qcm_unique, double_qcm_unique, enigme_texte)
 *     « Croire que la valeur absolue est le nombre sans son signe, et
 *       l'appliquer à une expression » → valeur_absolue_sans_signe (absolue_vf)
 *     Codes hors table (erreurs propres au registre, signalées dans le
 *     rapport) : oppose_confondu_avec_valeur_absolue (QCM sur l'opposé d'un
 *     positif), oppose_applique_une_fois / oppose_applique_deux_fois (chaîne
 *     d'opposés), distance_signee et graduation_voisine (droite graduée).
 *   Graduation → cartes :
 *     niveau 1 (entier relatif) → trou, absolue, double, droite (tirages
 *       entiers) ; niveau 2 (décimal) → trou, absolue, double (tirages
 *       décimaux) ; niveau 3 (tableau, nombre ou opposé masqué) → tableauva
 *       (format « ligne masquée »), droite (lecture d'abscisses) ; niveau 4
 *       (la valeur absolue est la donnée) → tableauva (format « retrouver »)
 *       et le tirage ligne masquée = 1 ; niveau 5 (énigme) → enigme.
 *   Non tranché : la graduation ne place les deux décimales qu'au niveau 5
 *     alors que nombre_de_decimales les autorise partout et que l'erreur visée
 *     « dixièmes / centièmes » les exige ; type_reponse annonce un barème
 *     explicite par case, que l'application ne rend pas ; type_reponse annonce
 *     « deux formats » quand la graduation en suppose davantage.
 *
 * Politique du cluster : signe « - » = tiret clavier PARTOUT (jamais U+2212) ;
 * aucune règle de calcul sur les relatifs ; jamais la notation à barres
 * verticales.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 911;

const L_TROU = 'De l\'addition à trou à l\'opposé';
const L_ABSOLUE = 'La valeur absolue d\'un nombre';
const L_DOUBLE = 'L\'opposé de l\'opposé';
const L_DROITE = 'Distance à zéro sur la droite graduée';
const L_TABLEAU = 'Nombre, valeur absolue et opposé';
const L_ENIGME = 'L\'énigme du nombre caché';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const CONSIGNE_DECIMAL = 'Écris ta réponse en écriture décimale.';

// ═════════ Écriture des nombres ═════════
// Les valeurs sont manipulées en CENTIÈMES entiers (zéro flottant) :
// cent(-347) = « -3,47 », cent(0) = « 0 » — jamais de zéro signé.

const cent = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const r = a % 100;
    if (r === 0) return `${s}${ent}`;
    if (r % 10 === 0) return `${s}${ent},${r / 10}`;
    return `${s}${ent},${String(r).padStart(2, '0')}`;
};
/** Écriture signée : le + est écrit devant un positif ; 0 n'est jamais signé. */
const signe = (c) => (c === 0 ? '0' : c < 0 ? cent(c) : `+${cent(c)}`);
/** Un négatif entre parenthèses dans une phrase ou une somme. */
const aff = (c) => (c < 0 ? `(${cent(c)})` : cent(c));
/** affichage_du_signe_des_positifs : le + est tiré CELLULE PAR CELLULE. */
const cellule = (c) => (c > 0 && randomChoice([true, false]) ? `+${cent(c)}` : cent(c));

/** Valeur absolue tirée dans les bornes de la spec : 0, 1 ou 2 décimales, non nulle. */
const tirerAbs = (dec) => {
    if (dec === 0) return randInt(1, 10) * 100;
    if (dec === 1) {
        let k = randInt(1, 99);
        if (k % 10 === 0) k += 1;
        return k * 10;
    }
    let k = randInt(1, 999);
    if (k % 10 === 0) k += 1;
    return k;
};
const tirerDec = () => randomChoice([0, 0, 1, 1, 2]);
/** Un nombre relatif dans les bornes ; zéro est tiré VOLONTAIREMENT (1 fois sur 8). */
const tirerNombre = (avecZero, dec) => {
    if (avecZero && randInt(1, 8) === 1) return 0;
    const v = tirerAbs(dec == null ? tirerDec() : dec);
    return randomChoice([true, false]) ? v : -v;
};
/** Niveau de graduation d'un tirage : entier → 1, décimal → 2. */
const niveauDe = (v) => (v % 100 === 0 ? 1 : 2);

/** Échange le chiffre des dixièmes et celui des centièmes (erreur visée 6). */
const echangeDecimales = (c) => {
    const s = c < 0 ? -1 : 1;
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const dix = Math.floor((a % 100) / 10);
    const cnt = a % 10;
    return s * (ent * 100 + cnt * 10 + dix);
};

// Dénominations : une série n'en emploie QU'UNE (points_de_vigilance).
const DENOMS = [
    { un: 'valeur absolue', la: 'la valeur absolue', des: 'valeurs absolues' },
    { un: 'distance à zéro', la: 'la distance à zéro', des: 'distances à zéro' },
];
const tirerDenom = () => randomChoice(DENOMS);

/** Tire nb vérités avec au moins une vraie et une fausse (mélange garanti). */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

/** n valeurs de valeurs absolues DISTINCTES, dans les bornes, zéro en option. */
const tirerValeursDistinctes = (n, avecZero) => {
    const abs = new Set();
    const vals = [];
    if (avecZero) { vals.push(0); abs.add(0); }
    for (let essai = 0; essai < 80 && vals.length < n; essai++) {
        const v = tirerNombre(false, null);
        if (!abs.has(Math.abs(v))) { abs.add(Math.abs(v)); vals.push(v); }
    }
    // Repli déterministe : valeurs dans les bornes, valeurs absolues distinctes.
    [-100, 250, -370, 800, -940, 600, -720, 430].forEach((v) => {
        if (vals.length < n && !abs.has(Math.abs(v))) { abs.add(Math.abs(v)); vals.push(v); }
    });
    return shuffle(vals);
};

// ═════════ Carte « trou » — De l'addition à trou à l'opposé ═════════

const genTrouEgalitesDrag = () => {
    const avecZero = randInt(1, 3) === 1;
    const vals = tirerValeursDistinctes(4, avecZero);
    // affichage_du_signe_des_positifs : le + est tiré par SÉRIE ici, car en mode
    // drag l'étiquette est comparée caractère pour caractère.
    const avecPlus = randomChoice([true, false]);
    const ecr = (v) => (avecPlus ? signe(v) : cent(v));
    // Une égalité par ligne (jamais « ; » entre deux égalités à trous :
    // l'impression coupait la ligne au milieu, convention 2026-09-02).
    const phrase = vals.map(v => `${aff(v)} + {{${ecr(-v)}}} = 0`).join('\n');
    // Le piège : recopier le nombre au lieu de changer son signe. Les valeurs
    // absolues étant distinctes, aucun candidat n'est filtré comme bonne réponse.
    const candidats = vals.filter(v => v !== 0).map(v => ecr(v));
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète chaque égalité. Deux nombres opposés ont pour somme 0.</b></p>',
        { ...opts(L_TROU), mode: 'drag', distracteurs });
    payload._meta.params = {
        vals,
        niveau: vals.every(v => v % 100 === 0) ? 1 : 2,
        distracteurs: distracteurs.map(d => ({ valeur: d, code: 'oppose_negatif_inchange' })),
    };
    return payload;
};

const genTrouOpposeSaisie = () => {
    const x = tirerNombre(true, null);
    const payload = makeSaisie(cent(-x),
        `<p><b>Quel est l'opposé de ${aff(x)} ?</b> ${CONSIGNE_DECIMAL}</p>`,
        opts(L_TROU));
    payload._meta.params = {
        x,
        niveau: niveauDe(x),
        ...(x === 0
            ? { erreurVisee: 'oppose_de_zero_signe' }
            : x < 0 ? { erreurVisee: 'oppose_negatif_inchange' } : {}),
    };
    return payload;
};

// ═════════ Carte « absolue » — La valeur absolue d'un nombre ═════════

const genAbsolueSaisie = () => {
    const d = tirerDenom();
    const x = tirerNombre(true, null);
    const payload = makeSaisie(cent(Math.abs(x)),
        `<p><b>Quelle est ${d.la} de ${aff(x)} ?</b> ${CONSIGNE_DECIMAL}</p>`,
        opts(L_ABSOLUE));
    payload._meta.params = {
        x,
        denom: d.un,
        niveau: niveauDe(x),
        ...(x < 0 ? { erreurVisee: 'valeur_absolue_recopie' } : {}),
    };
    return payload;
};

const genAbsolueQcm = () => {
    const d = tirerDenom();
    // Deux décimales, chiffres des dixièmes et des centièmes DISTINCTS et non
    // nuls : sans cela l'échange des décimales ne produirait pas de distracteur.
    const ent = randInt(0, 9);
    const dix = randInt(1, 9);
    let cnt = randInt(1, 9);
    for (let essai = 0; essai < 40 && cnt === dix; essai++) cnt = randInt(1, 9);
    if (cnt === dix) cnt = dix === 9 ? 1 : dix + 1; // repli déterministe
    const val = ent * 100 + dix * 10 + cnt;

    // Question tirée : la valeur absolue (le nombre est alors négatif, sans
    // quoi « recopier le nombre » donnerait la bonne réponse) ou l'opposé.
    const surAbsolue = randomChoice([true, false]);
    const x = surAbsolue || randomChoice([true, false]) ? -val : val;
    const correct = -x; // valeur absolue d'un négatif ET opposé : la même valeur
    const codeRecopie = surAbsolue
        ? 'valeur_absolue_recopie'
        : x < 0 ? 'oppose_negatif_inchange' : 'oppose_confondu_avec_valeur_absolue';
    const distracteurs = [
        { valeur: cent(x), code: codeRecopie },
        { valeur: cent(echangeDecimales(correct)), code: 'dixiemes_centiemes' },
        { valeur: cent(echangeDecimales(x)), code: 'dixiemes_centiemes' },
    ];
    const payload = makeQcm([cent(correct), ...distracteurs.map(t => t.valeur)], cent(correct),
        `<p><b>${surAbsolue ? `Quelle est ${d.la} de` : 'Quel est l\'opposé de'} ${aff(x)} ?</b></p>`,
        opts(L_ABSOLUE));
    payload._meta.params = { x, denom: d.un, surAbsolue, niveau: 2, distracteurs };
    return payload;
};

/**
 * Gabarits à prédicat variable : chaque gabarit produit une ligne vraie OU une
 * ligne fausse selon `v`. Le code nomme l'erreur d'élève rendue visible.
 * ctx : { d (dénomination), a (valeur absolue non nulle), p, q (p < q) }.
 */
const GABARITS_ABSOLUE = [
    {
        id: 'recopie', code: 'valeur_absolue_recopie',
        ligne: (v, x) => `${x.d.la[0].toUpperCase()}${x.d.la.slice(1)} de ${cent(-x.a)} est ${v ? cent(x.a) : cent(-x.a)}.`,
    },
    {
        id: 'meme_absolue', code: 'valeurs_absolues_differentes',
        ligne: (v, x) => (v
            ? `${cent(x.a)} et ${cent(-x.a)} ont la même ${x.d.un}.`
            : `${cent(x.a)} et ${cent(-x.a)} ont des ${x.d.des} opposées.`),
    },
    {
        id: 'zero', code: 'oppose_de_zero_signe',
        ligne: (v) => (v
            ? 'L\'opposé de 0 s\'écrit 0.'
            : 'L\'opposé de 0 s\'écrit avec un signe moins devant le 0.'),
    },
    {
        id: 'oppose_negatif', code: 'oppose_negatif_inchange',
        ligne: (v, x) => `L'opposé de ${cent(-x.a)} est ${v ? cent(x.a) : cent(-x.a)}.`,
    },
    {
        id: 'ordre', code: 'ordre_conserve_par_oppose',
        ligne: (v, x) => `Comme ${cent(x.p)} est plus petit que ${cent(x.q)}, l'opposé de ${cent(x.p)} est plus `
            + `${v ? 'grand' : 'petit'} que l'opposé de ${cent(x.q)}.`,
    },
    {
        id: 'expression', code: 'valeur_absolue_sans_signe',
        ligne: (v, x) => `${x.d.la[0].toUpperCase()}${x.d.la.slice(1)} de l'opposé de ${cent(-x.a)} est `
            + `${v ? cent(x.a) : cent(-x.a)}.`,
    },
];

const genAbsolueVf = () => {
    const d = tirerDenom();
    const a = tirerAbs(tirerDec());
    let p = tirerAbs(tirerDec());
    let q = tirerAbs(tirerDec());
    if (p === q) q = q < 1000 ? q + 10 : q - 10; // repli déterministe
    if (p > q) { const t = p; p = q; q = t; }
    const ctx = { d, a, p, q };
    const gabarits = shuffle(GABARITS_ABSOLUE).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_ABSOLUE));
    payload._meta.params = { denom: d.un, a, p, q, niveau: niveauDe(a), lignes };
    return payload;
};

// ═════════ Carte « double » — L'opposé de l'opposé ═════════

const genDoubleChaine = () => {
    const [x1, x2] = tirerValeursDistinctes(2, false);
    const avecZero = randomChoice([true, false, false]);
    const segments = [
        `nombre : ${aff(x1)} → opposé : {{${cent(-x1)}}} → opposé de l'opposé : {{${cent(x1)}}}`,
        `nombre : {{${cent(x2)}}} → opposé : ${aff(-x2)} → opposé de l'opposé : {{${cent(x2)}}}`,
    ];
    if (avecZero) segments.push('nombre : 0 → opposé : {{0}} → opposé de l\'opposé : {{0}}');
    const payload = makeTexteTrou(shuffle(segments).join('\n'), // une chaîne par ligne
        `<p><b>Complète les chaînes d'opposés.</b> ${CONSIGNE_DECIMAL}</p>`,
        { ...opts(L_DOUBLE), mode: 'saisie' });
    payload._meta.params = {
        x1, x2, avecZero, niveau: niveauDe(x1) === 1 && niveauDe(x2) === 1 ? 1 : 2,
    };
    return payload;
};

const genDoubleRegleDrag = () => {
    const x = tirerNombre(false, null);
    const phrase = `Le nombre ${aff(x)} a pour opposé ${aff(-x)}, et l'opposé de ${aff(-x)} est {{${cent(x)}}}.\n`
        + 'Pour tout nombre relatif, l\'opposé de l\'opposé est {{le nombre de départ}}.';
    const candidats = [cent(-x), '0', 'l\'opposé du nombre de départ', 'la distance à zéro du nombre de départ'];
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète : que vaut l\'opposé de l\'opposé ?</b></p>',
        { ...opts(L_DOUBLE), mode: 'drag', distracteurs });
    payload._meta.params = {
        x, niveau: niveauDe(x),
        distracteurs: distracteurs.map(t => ({ valeur: t, code: 'oppose_applique_une_fois' })),
    };
    return payload;
};

const genDoubleQcm = () => {
    // Deux décimales à chiffres distincts : l'échange des décimales est un
    // distracteur (erreur visée « dixièmes / centièmes »).
    const ent = randInt(0, 9);
    const dix = randInt(1, 9);
    let cnt = randInt(1, 9);
    for (let essai = 0; essai < 40 && cnt === dix; essai++) cnt = randInt(1, 9);
    if (cnt === dix) cnt = dix === 9 ? 1 : dix + 1; // repli déterministe
    const val = ent * 100 + dix * 10 + cnt;
    const x = randomChoice([true, false]) ? val : -val;
    const triple = randomChoice([true, false]);
    const correct = triple ? -x : x;
    const distracteurs = [
        { valeur: cent(triple ? x : -x), code: triple ? 'oppose_applique_deux_fois' : 'oppose_applique_une_fois' },
        { valeur: cent(echangeDecimales(correct)), code: 'dixiemes_centiemes' },
        { valeur: cent(echangeDecimales(-correct)), code: 'dixiemes_centiemes' },
    ];
    const payload = makeQcm([cent(correct), ...distracteurs.map(t => t.valeur)], cent(correct),
        `<p><b>Quel est l'opposé de l'opposé ${triple ? 'de l\'opposé ' : ''}de ${aff(x)} ?</b></p>`,
        opts(L_DOUBLE));
    payload._meta.params = { x, triple, niveau: 2, distracteurs };
    return payload;
};

// ═════════ Carte « droite » — Distance à zéro sur la droite graduée ═════════

const LETTRES_PTS = ['A', 'B', 'C', 'D', 'F', 'J'];
const DENOM_DROITE = DENOMS[1]; // le libellé de la carte fixe la dénomination

/** 6 points entiers de -9 à 9 dont 2 paires opposées ; abscisses distinctes. */
const tirerDroite = () => {
    const p1 = randInt(2, 9);
    let p2 = randInt(2, 9);
    if (p2 === p1) p2 = p1 === 9 ? 2 : p1 + 1; // repli déterministe
    const seuls = [];
    for (let essai = 0; essai < 60 && seuls.length < 2; essai++) {
        const v = randomChoice([true, false]) ? randInt(1, 9) : -randInt(1, 9);
        if (![p1, -p1, p2, -p2, ...seuls].includes(v)) seuls.push(v);
    }
    for (let v = 1; v <= 9 && seuls.length < 2; v++) { // repli exhaustif
        [v, -v].forEach((w) => {
            if (seuls.length < 2 && ![p1, -p1, p2, -p2, ...seuls].includes(w)) seuls.push(w);
        });
    }
    const valeurs = shuffle([p1, -p1, p2, -p2, ...seuls]);
    const lettres = shuffle(LETTRES_PTS);
    const points = valeurs.map((v, i) => ({ lettre: lettres[i], v }));
    return { points, paires: [[p1, -p1], [p2, -p2]] };
};

const elementDroite = (points) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start: -10, end: 10, step: 1, reperes: [0],
            points: points.map(p => ({ value: p.v, label: p.lettre, showValue: false })),
        },
    }],
});

const lettreDe = (points, v) => points.find(p => p.v === v).lettre;

const genDroiteAbscisseDrag = () => {
    const { points } = tirerDroite();
    const pt = randomChoice(points.filter(p => p.v < 0)); // point NÉGATIF : abscisse et distance discernables
    const autre = randomChoice(points.filter(p => p !== pt));
    // Une question par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // trous indépendants, l'impression coupait entre « est » et la case).
    const phrase = `Le point ${pt.lettre} a pour abscisse {{${cent(pt.v * 100)}}}.\n`
        + `Le signe de cette abscisse est {{-}}.\n`
        + `Sa ${DENOM_DROITE.un} est {{${cent(-pt.v * 100)}}}.`;
    const candidats = ['+', cent(Math.abs(autre.v) * 100), cent(-Math.abs(autre.v) * 100)];
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        '<p><b>Observe la droite graduée et complète.</b></p>',
        { ...opts(L_DROITE), mode: 'drag', distracteurs, elements_additionnels: elementDroite(points) });
    payload._meta.params = {
        pt: pt.v, niveau: 3,
        distracteurs: distracteurs.map(t => ({ valeur: t, code: 'oppose_negatif_inchange' })),
    };
    return payload;
};

/**
 * Gabarits à prédicat variable de la droite graduée : chacun produit une ligne
 * VRAIE ou FAUSSE selon `v`, jamais une proposition à statut fixe.
 * ctx : { points, paires }.
 */
const GABARITS_DROITE = [
    {
        id: 'signe', code: 'oppose_negatif_inchange',
        ligne: (v, ctx) => {
            const pt = ctx.pt1;
            const positif = v ? pt.v > 0 : pt.v < 0;
            return `L'abscisse du point ${pt.lettre} est ${positif ? 'positive' : 'négative'}.`;
        },
    },
    {
        id: 'distance', code: 'distance_signee',
        ligne: (v, ctx) => {
            const pt = ctx.pt2;
            const a = Math.abs(pt.v);
            // La valeur fausse reste dans les bornes (-10..10) : on s'écarte
            // vers le bas quand s'écarter vers le haut sortirait de la plage.
            const fausse = a + ctx.ecart <= 10 ? a + ctx.ecart : a - ctx.ecart;
            return `La ${DENOM_DROITE.un} de l'abscisse du point ${pt.lettre} est ${v ? a : fausse}.`;
        },
    },
    {
        id: 'opposees', code: 'valeurs_absolues_differentes',
        ligne: (v, ctx) => {
            const [o1] = ctx.paires[0];
            const autre = v ? -o1 : ctx.horsPaire1;
            return `Les abscisses des points ${lettreDe(ctx.points, o1)} et ${lettreDe(ctx.points, autre)} sont opposées.`;
        },
    },
    {
        id: 'meme_distance', code: 'valeurs_absolues_differentes',
        ligne: (v, ctx) => {
            const [, o2] = ctx.paires[1];
            const autre = v ? -o2 : ctx.horsPaire2;
            return `Les abscisses des points ${lettreDe(ctx.points, o2)} et ${lettreDe(ctx.points, autre)} ont la même ${DENOM_DROITE.un}.`;
        },
    },
    {
        id: 'oppose_abscisse', code: 'oppose_negatif_inchange',
        ligne: (v, ctx) => `L'opposé de l'abscisse du point ${ctx.pt3.lettre} est ${cent((v ? -ctx.pt3.v : ctx.pt3.v) * 100)}.`,
    },
];

const genDroiteVf = () => {
    const { points, paires } = tirerDroite();
    const [o1] = paires[0];
    const [, o2] = paires[1];
    const ctx = {
        points,
        paires,
        pt1: randomChoice(points),
        pt2: randomChoice(points),
        pt3: randomChoice(points),
        ecart: randInt(1, 2),
        horsPaire1: points.find(p => p.v !== o1 && p.v !== -o1).v,
        horsPaire2: points.find(p => p.v !== o2 && p.v !== -o2).v,
    };
    const gabarits = shuffle(GABARITS_DROITE).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Observe la droite graduée. Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        { ...opts(L_DROITE), elements_additionnels: elementDroite(points) });
    payload._meta.params = { valeurs: points.map(p => p.v), niveau: 3, lignes };
    return payload;
};

const genDroiteOpposesQcmMulti = () => {
    const { points, paires } = tirerDroite();
    const corrects = paires.map(([a, b]) => `${lettreDe(points, a)} et ${lettreDe(points, b)}`);
    const faux = [];
    // parcours exhaustif mélangé des paires non opposées (borné par construction)
    const indices = shuffle(points.flatMap((_, i) => points.map((_, j) => [i, j]).filter(([a, b]) => a < b)));
    for (const [i, j] of indices) {
        if (faux.length >= 2) break;
        const x = points[i];
        const y = points[j];
        if (x.v !== -y.v) {
            const paire = `${x.lettre} et ${y.lettre}`;
            if (!corrects.includes(paire)) faux.push(paire);
        }
    }
    const payload = makeQcm([...corrects, ...faux], corrects,
        '<p><b>Coche les deux paires de points dont les abscisses sont opposées.</b></p>',
        { ...opts(L_DROITE), elements_additionnels: elementDroite(points) }, true);
    payload._meta.params = {
        paires, niveau: 3,
        distracteurs: faux.map(t => ({ valeur: t, code: 'valeurs_absolues_differentes' })),
    };
    return payload;
};

const genDroiteDistanceSaisie = () => {
    const { points } = tirerDroite();
    const pt = randomChoice(points);
    const payload = makeSaisie(cent(Math.abs(pt.v) * 100),
        `<p><b>Quelle est la ${DENOM_DROITE.un} de l'abscisse du point ${pt.lettre} ?</b></p>`,
        { ...opts(L_DROITE), elements_additionnels: elementDroite(points) });
    payload._meta.params = {
        pt: pt.v, niveau: 3,
        ...(pt.v < 0 ? { erreurVisee: 'valeur_absolue_recopie' } : {}),
    };
    return payload;
};

// ═════════ Carte « tableauva » — Nombre, valeur absolue et opposé ═════════
// Une COLONNE du tableau par ligne de phrase ; la LIGNE masquée du tableau
// (le nombre, sa valeur absolue ou son opposé) est le trou de chaque ligne.

const genTableauLigneMasquee = () => {
    const d = tirerDenom();
    const nbColonnes = randInt(4, 8);          // nombre_de_colonnes_du_tableau
    const masquee = randInt(0, 2);             // ligne_masquee_du_tableau
    const vals = tirerValeursDistinctes(nbColonnes, randInt(1, 3) === 1);
    const phrase = vals.map((v) => {
        // Une valeur absolue n'est jamais écrite avec un signe + : le tirage du
        // signe (affichage_du_signe_des_positifs) ne porte que sur le nombre et
        // sur son opposé.
        const cases = [
            ['nombre', v, cellule],
            [d.un, Math.abs(v), cent],
            ['opposé', -v, cellule],
        ];
        return cases
            .map(([lab, val, ecr], i) => `${lab} : ${i === masquee ? `{{${cent(val)}}}` : ecr(val)}`)
            .join(' → ');
    }).join('\n');
    const payload = makeTexteTrou(phrase,
        `<p><b>Complète le tableau : chaque ligne donne un nombre, sa ${d.un} et son opposé.</b> `
        + `${CONSIGNE_DECIMAL} Chaque case compte pour un point.</p>`,
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = { vals, denom: d.un, masquee, nbColonnes, niveau: 3 };
    return payload;
};

const genTableauRetrouver = () => {
    const d = tirerDenom();
    const nbColonnes = randInt(4, 5); // deux lignes masquées : on resserre le tableau
    // Tous les nombres sont NÉGATIFS : la consigne lève l'ambiguïté des deux
    // solutions opposées (points_de_vigilance). Zéro est exclu ici, il rendrait
    // la consigne fausse.
    const vals = tirerValeursDistinctes(nbColonnes, false).map(v => -Math.abs(v));
    // La contrainte « négatif » est REPRISE DANS CHAQUE LIGNE, et pas seulement dans la
    // consigne. Une ligne se lit seule : « distance à zéro : 5,3 → nombre : … » laisse
    // croire que +5,3 convient, et il convient en effet si on oublie la phrase posée
    // cinq lignes plus haut. Le prof s'y est laissé prendre le 2026-09-25, un élève
    // s'y laissera prendre aussi.
    const phrase = vals.map(v => `${d.un} : ${cent(Math.abs(v))} → nombre négatif : {{${cent(v)}}} → son opposé : {{${cent(-v)}}}`)
        .join('\n');
    const payload = makeTexteTrou(phrase,
        `<p><b>Complète le tableau.</b> Dans ce tableau, tous les nombres sont négatifs. `
        + `${CONSIGNE_DECIMAL} Chaque case compte pour un point.</p>`,
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = { vals, denom: d.un, nbColonnes, niveau: 4 };
    return payload;
};

// ═════════ Carte « enigme » — L'énigme du nombre caché ═════════

const genEnigme = () => {
    const d = tirerDenom();
    const negatif = randomChoice([true, false]);
    const unite = randInt(1, 8);
    const typeRelation = randomChoice(['double', 'ecart']);
    let cnt;
    let dix;
    let ecart = 0;
    if (typeRelation === 'double') {
        cnt = randInt(1, 3);
        dix = 2 * cnt;
    } else {
        ecart = randInt(1, 4);
        cnt = randInt(1, Math.floor((9 - ecart) / 2));
        dix = cnt + ecart;
    }
    const somme = dix + cnt;
    const val = unite * 100 + dix * 10 + cnt;
    const x = negatif ? -val : val;

    const relation = typeRelation === 'double'
        ? 'Son chiffre des dixièmes est le double de son chiffre des centièmes.'
        : `Son chiffre des dixièmes vaut ${ecart} de plus que son chiffre des centièmes.`;
    const texte = '<p><b>Trouve le nombre caché.</b></p>'
        + `<p>Son opposé est un nombre ${negatif ? 'positif' : 'négatif'}.</p>`
        + `<p>Sa ${d.un} est comprise entre ${unite} et ${unite + 1}.</p>`
        + `<p>${relation}</p>`
        + `<p>La somme de son chiffre des dixièmes et de son chiffre des centièmes vaut ${somme}.</p>`
        + `<p>${CONSIGNE_DECIMAL}</p>`;

    const payload = makeSaisie(cent(x), texte, opts(L_ENIGME));
    payload._meta.params = {
        x, denom: d.un, unite, dix, cnt, somme, typeRelation, ecart,
        niveau: 5, erreurVisee: 'dixiemes_centiemes',
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    trou_egalites_texte_trou_drag: genTrouEgalitesDrag,
    trou_oppose_texte: genTrouOpposeSaisie,
    absolue_texte: genAbsolueSaisie,
    absolue_qcm_unique: genAbsolueQcm,
    absolue_vf: genAbsolueVf,
    double_chaine_texte_trou_saisie: genDoubleChaine,
    double_regle_texte_trou_drag: genDoubleRegleDrag,
    double_qcm_unique: genDoubleQcm,
    droite_abscisse_texte_trou_drag: genDroiteAbscisseDrag,
    droite_vf: genDroiteVf,
    droite_opposes_qcm_multiple: genDroiteOpposesQcmMulti,
    droite_distance_texte: genDroiteDistanceSaisie,
    tableauva_ligne_masquee_texte_trou_saisie: genTableauLigneMasquee,
    tableauva_retrouver_texte_trou_saisie: genTableauRetrouver,
    enigme_texte: genEnigme,
};

const FORMAT_LABELS = {
    egalites_texte_trou_drag: 'Drag', oppose_texte: 'Saisie',
    texte: 'Saisie', qcm_unique: 'QCM', vf: 'Vrai/Faux',
    chaine_texte_trou_saisie: 'Saisie trous', regle_texte_trou_drag: 'Drag',
    abscisse_texte_trou_drag: 'Drag', opposes_qcm_multiple: 'QCM Multi',
    distance_texte: 'Saisie',
    ligne_masquee_texte_trou_saisie: 'Saisie trous',
    retrouver_texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'trou', label: L_TROU, formatsList: buildFormats('trou') },
    { id: 'absolue', label: L_ABSOLUE, formatsList: buildFormats('absolue') },
    { id: 'double', label: L_DOUBLE, formatsList: buildFormats('double') },
    { id: 'droite', label: L_DROITE, formatsList: buildFormats('droite') },
    { id: 'tableauva', label: L_TABLEAU, formatsList: buildFormats('tableauva') },
    { id: 'enigme', label: L_ENIGME, formatsList: buildFormats('enigme') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
